import { Component, OnInit, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Store } from "@ngrx/store";
import { DeliveryService } from "../shared/services/delivery.service";
import {
  DistributionWaybillsResponse,
  DistributionWaybillsSearchParams,
} from "../shared/models/delivery.model";
import { LoaderComponent } from "../shared/components/ui/loader/loader.component";
import { TranslateDirective, TranslateModule, TranslateService } from "@ngx-translate/core";
import {
  deliverersList,
  distributionWaybills,
  distributionWaybillsSearchMetadata,
} from "../shared/ngrx/ngrx-selectors";
import { TableComponent } from "../shared/components/ui/table/table.component";
import { ServiceMetaData } from "../shared/models/service-meta.model";
import { DataElement, PaginationParams } from "../shared/models/common";
import { SelectModule } from "primeng/select";
import { InputWrapComponent } from "../shared/components/ui/input-wrap.component";
import { SpinnerComponent } from "../shared/components/ui/spinner/spinner.component";
import { ClearMultiComponent } from "../shared/components/ui/clear-multi.component";

@Component({
  selector: "app-delivery-wb",
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    LoaderComponent,
    TranslateModule,
    SelectModule,
    InputWrapComponent,
    SpinnerComponent,
    ClearMultiComponent,
  ],
  templateUrl: "./delivery-wb.html",
  styleUrl: "./delivery-wb.scss",
  standalone: true,
})
export class DeliveryWb implements OnInit {
  distributionWaybills: DistributionWaybillsResponse | null = null;
  loading: boolean = false;
  searchParams: DistributionWaybillsSearchParams;
  numberOfRows: number = 15;
  rangeFrom: number = 1;
  rangeTo: number = this.numberOfRows;
  manualOrders: string[] = [];
  manualOrderInput = "";
  printing: boolean = false;
  selectedDeliverer: DataElement;
  deliverersList$ = this.store.select(deliverersList);

  @ViewChild("waybillsTable") waybillsTable: TableComponent;

  constructor(
    private readonly store: Store,
    private deliveryService: DeliveryService,
    private readonly translateService: TranslateService
  ) {
    this.resetSearchParams();
  }

  ngOnInit(): void {
    this.loadData(true);

    // Subscribe to store changes
    this.store.select(distributionWaybills).subscribe((data) => {
      if (data) {
        this.distributionWaybills = data;
      }
    });
  }

  get searchMetaData(): ServiceMetaData {
    return this.store.selectSignal(distributionWaybillsSearchMetadata)() || {};
  }

  resetSearchParams(): void {
    this.searchParams = {
      rangeTo: this.rangeTo,
      rangeFrom: this.rangeFrom,
      searchId: null,
    };

    this.manualOrders = [];
  }

  loadData(newSearch: boolean = false): void {
    if (newSearch) {
      this.resetSearchParams();
      this.waybillsTable?.reset();
    }

    this.loading = true;
    this.deliveryService.getDistributionWaybills(this.searchParams).subscribe({
      next: () => {
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  loadDataOnPagination(event: PaginationParams): void {
    if (
      !this.waybills ||
      (event.rangeFrom == this.searchParams.rangeFrom &&
        event.rangeTo == this.searchParams.rangeTo)
    )
      return;

    this.searchParams.rangeFrom = event.rangeFrom;
    this.searchParams.rangeTo = event.rangeTo;
    this.searchParams.searchId = this.searchMetaData?.searchId;

    this.loadData(false);
  }


  get waybills(): any[] {
    return this.distributionWaybills?.waybills ?? [];
  }

  getOrdersWithStyling(waybill: any): any[] {
    if (!waybill?.orders) return [];

    return waybill.orders.map((order: any) => ({
      ...order,
      rowGrey: order.displayType !== "READY",
    }));
  }


  addManualOrder(): void {
    const value = (this.manualOrderInput || "").trim();
    if (!value) return;
    this.manualOrders = [...this.manualOrders, value];
    this.manualOrderInput = "";
  }

  removeManualOrder(index: number): void {
    this.manualOrders = this.manualOrders.filter((_, i) => i !== index);
  }

  onPrint(): void {
    if (this.printing) {
      return;
    }

    this.printing = true;

    this.deliveryService
      .createDeliveryWaybills(this.manualOrders, this.selectedDeliverer?.id)
      .subscribe({
      next: (response) => {
        this.printing = false;
        this.loadData(true);
        if (response.waybillsPdfUrl) {
          this.downloadAndPrintPdf(response.waybillsPdfUrl);
        }
      },
      error: () => {
        this.printing = false;
      },
    });
  }

  private downloadAndPrintPdf(url: string): void {
    // Create a temporary anchor element to download the file
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.download = "waybills_" + new Date().toISOString() + ".pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Open PDF in new window and trigger print dialog
    const printWindow = window.open(url, "_blank");
    if (printWindow) {
      // Use a longer timeout to ensure PDF is fully loaded
      setTimeout(() => {
        try {
          printWindow.focus();
          printWindow.print();
        } catch (error) {
          console.error("Error triggering print:", error);
        }
      }, 1000);
    }
  }

}
